/**
 * RightTray — v3 AI assistant side panel.
 * Spec §1.6: 260px, sparkle header, AI chat, input bar.
 * Shares canvas gradient background. Left zone divider border.
 * Mobile overlay: full-screen, instant (no animation per spec §6 non-goals).
 * Mobile a11y: focus trap + body scroll lock.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, Settings, X, Loader2 } from 'lucide-react';
import { getApiUrl, tokenManager } from '../api/client';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

interface RightTrayProps {
  /** When true, renders as full-screen mobile overlay instead of side panel */
  isMobile?: boolean;
  /** Close callback for mobile overlay (X button, Escape) */
  onClose?: () => void;
}

export function RightTray({ isMobile, onClose }: RightTrayProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const messageCounter = useRef(0);

  // Body scroll lock for mobile overlay — prevents background scroll on iOS Safari
  useEffect(() => {
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile]);

  // Auto-focus input on mount (mobile focuses overlay container for Escape handling)
  useEffect(() => {
    if (isMobile) {
      overlayRef.current?.focus();
    }
  }, [isMobile]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus trap for mobile overlay — Tab cycles between close button and input
  const handleOverlayKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose?.();
      return;
    }
    if (e.key !== 'Tab' || !isMobile) return;

    const focusable = [closeRef.current, inputRef.current].filter(Boolean) as HTMLElement[];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [isMobile, onClose]);

  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const userMsg: Message = {
      id: `msg-${messageCounter.current++}`,
      role: 'user',
      text,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const token = tokenManager.getToken();
      const response = await fetch(getApiUrl('/chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text }),
      });

      let assistantText: string;
      if (response.ok) {
        const data = await response.json() as { reply: string };
        assistantText = data.reply;
      } else {
        assistantText = 'Sorry, I could not process your request. Please try again.';
      }

      const assistantMsg: Message = {
        id: `msg-${messageCounter.current++}`,
        role: 'assistant',
        text: assistantText,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch {
      const errorMsg: Message = {
        id: `msg-${messageCounter.current++}`,
        role: 'assistant',
        text: 'Connection error. Please check your network and try again.',
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const hasMessages = messages.length > 0;

  const trayContent = (
    <>
      {/* Header */}
      <div
        data-testid="right-tray-header"
        className="flex items-center gap-1.5 px-3 py-2.5
          border-b border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.05)]"
      >
        <Sparkles size={14} className="text-t1" />
        <span className="text-whisper-sm font-semibold text-t1 text-etched flex-1">
          AI Assistant
        </span>
        {isMobile ? (
          <button
            ref={closeRef}
            type="button"
            aria-label="Close AI Assistant"
            data-testid="right-tray-close"
            className="text-t2 hover:text-t1"
            onClick={onClose}
          >
            <X size={14} />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Assistant settings"
            className="text-t2 hover:text-t1"
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {/* Chat body */}
      <div
        ref={chatRef}
        data-testid="right-tray-chat"
        className="flex-1 overflow-y-auto px-3 py-2"
      >
        {!hasMessages && !isLoading && (
          <div
            data-testid="right-tray-placeholder"
            className="flex flex-col items-center justify-center h-full text-center"
          >
            <Sparkles size={28} className="text-t2 opacity-40 mb-2" />
            <div className="text-whisper-sm text-t2 text-etched">AI Assistant</div>
            <div className="text-whisper-xs text-t2 text-etched opacity-60 mt-1">
              Ask about policies, findings, or compliance
            </div>
          </div>
        )}
        {hasMessages && (
          <div className="flex flex-col gap-2">
            {messages.map((msg, i) => (
              <div
                key={msg.id}
                data-testid={`right-tray-message-${i}`}
                className={`max-w-[85%] px-2.5 py-1.5 rounded-glass text-whisper-sm text-etched ${
                  msg.role === 'user'
                    ? 'self-end bg-[rgba(0,0,0,0.06)] dark:bg-[rgba(255,255,255,0.06)] text-t1'
                    : 'self-start text-t2'
                }`}
              >
                {msg.text}
              </div>
            ))}
            {isLoading && (
              <div className="self-start flex items-center gap-1.5 text-t2 text-whisper-sm text-etched px-2.5 py-1.5">
                <Loader2 size={12} className="animate-spin" />
                Thinking...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="px-3 py-2 border-t border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.05)]">
        <input
          ref={inputRef}
          type="text"
          data-testid="right-tray-input"
          placeholder="Ask about this policy..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          className="w-full px-2.5 py-1.5 rounded-lg text-whisper-sm text-t1 text-etched
            bg-[#f5f3f0] dark:bg-[#1e2435]
            border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.08)]
            placeholder:text-t2 outline-none
            focus:border-[rgba(0,0,0,0.2)] dark:focus:border-[rgba(255,255,255,0.15)]
            disabled:opacity-50"
        />
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div
        ref={overlayRef}
        data-testid="right-tray-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="AI Assistant"
        className="fixed inset-0 z-50"
        onKeyDown={handleOverlayKeyDown}
        tabIndex={-1}
      >
        {/*
          Backdrop — blocks pointer events from reaching underlying page (fixes
          "click-through" reported in v2.4.14 handoff) and closes the tray when
          tapped outside the panel. Mirrors DocumentUploadModal/VARShareModal pattern.
        */}
        {/*
          Decorative backdrop. Keyboard users close via Escape (handled on
          overlayRef) or the explicit close button above — so the backdrop
          does not need keyboard affordances. aria-hidden avoids lying to
          screen readers about it being a button.
        */}
        <div
          data-testid="right-tray-backdrop"
          className="absolute inset-0 bg-black/30 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          data-testid="right-tray-panel"
          className="absolute inset-0 z-10 flex flex-col bg-[#f5f3f0] dark:bg-[#1e2435]"
        >
          {trayContent}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="right-tray"
      className="w-[260px] flex-shrink-0 flex flex-col sticky top-0
        h-[calc(100vh-40px)] xl:h-[calc(100vh-34px)]
        border-l border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.08)]"
    >
      {trayContent}
    </div>
  );
}
